import type { SgdbSearchResult } from '@mcgames/contracts';
import { Button, Spinner, TextInput } from '@mcgames/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../../api/hooks.ts';
import { ErrorText } from './ErrorText.tsx';
import styles from './Games.module.css';

export type SgdbSearchProps = {
  /** Prefills the search field, usually the game title. */
  initialQuery: string;
  /** Disables the results while the pick is being saved. */
  busy: boolean;
  /** The error of whatever `onPick` started, shown below the search. */
  error: unknown;
  onPick: (result: SgdbSearchResult) => void;
};

/** Searches games on SteamGridDB and lists them as buttons. */
export function SgdbSearch({ initialQuery, busy, error, onPick }: SgdbSearchProps) {
  const { t } = useTranslation('admin');
  const [query, setQuery] = useState(initialQuery);
  const [submitted, setSubmitted] = useState('');
  const results = useApiQuery<SgdbSearchResult[]>(
    `/api/admin/metadata/steamgriddb/search?q=${encodeURIComponent(submitted)}`,
    { enabled: submitted !== '' },
  );

  function search(event: FormEvent) {
    event.preventDefault();
    setSubmitted(query.trim());
  }

  return (
    <>
      <form className={styles['row']} onSubmit={search}>
        <div className={styles['grow']}>
          <TextInput
            label={t('games.sgdb.queryLabel')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={query.trim() === ''}>
          {t('games.sgdb.search')}
        </Button>
      </form>
      {results.isFetching && <Spinner />}
      <ErrorText error={results.error ?? error} />
      {results.data?.length === 0 && <p className={styles['muted']}>{t('games.sgdb.noResults')}</p>}
      {results.data !== undefined && results.data.length > 0 && (
        <ul className={styles['results']}>
          {results.data.map((result) => (
            <li key={result.id}>
              <button
                type="button"
                className={styles['result']}
                disabled={busy}
                onClick={() => onPick(result)}
              >
                {result.releaseYear !== null
                  ? `${result.name} (${result.releaseYear})`
                  : result.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
